/**
 * New-seed misting: the pure decision logic behind {@link NewSeedProgram}.
 *
 * A zone in this mode is watered on its own track, not by the session
 * planner/executor; see the type's doc comment for why. Everything here is a
 * pure function of the program, the local date, and how far the day has already
 * got, so the backend scheduler only has to persist the answer.
 *
 * The two rules that matter:
 *
 *  - **Cycles are handled strictly in order.** The day's record counts how many
 *    of `times` have been dealt with, and that count only ever moves forward.
 *    Nothing re-reads the clock to decide which cycle is "current", so a
 *    duplicate tick can never re-fire one.
 *  - **A stale cycle expires rather than fires.** Seed misting is about keeping
 *    the seedbed damp at the right times of day; a cycle that missed its slot by
 *    hours (a Lambda outage, a controller that was busy all morning) is worse
 *    than useless applied late: it just wets the bed at the wrong time and
 *    counts against the day's water cap. So past {@link SEED_CYCLE_GRACE_MINUTES}
 *    we step over it without watering.
 */
import { hhmmToMinutes } from './capacity.js';
/**
 * How late a misting cycle may still fire, in minutes past its scheduled local
 * time. Comfortably longer than the executor's 5-minute cadence (so an ordinary
 * missed tick or a busy controller still gets the water in), short enough that
 * a morning outage can't dump the 9am mist on the lawn at 5pm.
 */
export const SEED_CYCLE_GRACE_MINUTES = 90;
/** Is this zone's new-seed program running on `localDate`? */
export function newSeedActive(zone, localDate) {
    const program = zone.newSeed;
    if (!program)
        return false;
    // YYYY-MM-DD compares correctly as a string, which keeps this free of any
    // timezone reasoning: the caller already resolved the tenant's local date.
    return program.startDate <= localDate && localDate <= program.endDate;
}
/** Total valve minutes a full day of the program applies. */
export function newSeedDailyMinutes(program) {
    return program.times.length * program.runMinutes;
}
/** Gross depth a full day of the program applies to a zone, inches. */
export function newSeedDailyDepthIn(program, zone) {
    return (newSeedDailyMinutes(program) / 60) * zone.precipRateInPerHr;
}
/**
 * The day's misting cycles, expressed as the {@link Cycle} list a run record
 * carries.
 *
 * Each cycle's `soakMinutes` is the idle gap until the NEXT mist, which is not
 * decoration: everything that reads a run's pulse clock (notably `runActivity`)
 * recovers the real valve-close time as `nextPulseAt − soakMinutes`. Writing
 * the gap here is what keeps a misting run from claiming to be watering for the
 * three hours between cycles. The last cycle has no gap, so its soak is 0.
 */
export function newSeedCycles(program) {
    const minutes = program.times.map(hhmmToMinutes);
    return minutes.map((start, i) => {
        const next = minutes[i + 1];
        const gap = next === undefined ? 0 : Math.max(0, next - start - program.runMinutes);
        return { runMinutes: program.runMinutes, soakMinutes: gap };
    });
}
/**
 * Map a day's handled-cycle count onto an edited program's times.
 *
 * `handled` is an INDEX into the times the day's record was written with, so a
 * program edited mid-day makes it mean the wrong thing: insert an earlier time
 * and index 1 slides onto a cycle that already misted (re-firing it), remove a
 * fired one and it slides onto a cycle still due (dropping it).
 *
 * The count is re-derived from the only fact that survives an edit: the LOCAL
 * TIME of the last cycle already dealt with. Everything in the new list at or
 * before that time is treated as handled, everything after it is still due. So
 * an inserted 08:00 is handled along with the 09:00 that really fired, and a
 * deleted 09:00 leaves nothing before noon handled, which is correct: the
 * noon cycle has not run.
 *
 * @param previousTimes the times the record's count indexes into
 * @param currentTimes the program's times now
 * @param handled how many cycles the record has dealt with
 */
export function reconcileSeedHandled(params) {
    const { previousTimes, currentTimes } = params;
    const handled = Math.max(0, Math.min(params.handled, previousTimes.length));
    if (handled === 0)
        return 0;
    const lastHandledMinute = hhmmToMinutes(previousTimes[handled - 1]);
    return currentTimes.filter((t) => hhmmToMinutes(t) <= lastHandledMinute).length;
}
/**
 * Decide this tick's misting action for one zone.
 *
 * @param program the zone's program
 * @param localDate the tenant's current local date
 * @param minuteOfDay the tenant's current local minute-of-day
 * @param handled how many cycles the day's record has already dealt with
 */
export function newSeedDueCycles(params) {
    const { program, localDate, minuteOfDay } = params;
    const handled = Math.max(0, Math.min(params.handled, program.times.length));
    const nothing = (at) => {
        const next = program.times[at];
        return {
            expired: [],
            handled: at,
            ...(next !== undefined ? { nextAtMinute: hhmmToMinutes(next) } : {}),
        };
    };
    // Outside the program's dates nothing is ever due, not even expiry, since
    // there is no day to catch up on.
    if (localDate < program.startDate || localDate > program.endDate)
        return nothing(handled);
    const expired = [];
    let fire;
    let i = handled;
    for (; i < program.times.length; i++) {
        const atMinute = hhmmToMinutes(program.times[i]);
        // Not yet its time: this cycle and everything after it stays unhandled.
        if (minuteOfDay < atMinute)
            break;
        if (minuteOfDay - atMinute > SEED_CYCLE_GRACE_MINUTES) {
            expired.push(i);
            continue;
        }
        fire = { index: i, atMinute };
        i++;
        break;
    }
    const nextTime = program.times[i];
    return {
        expired,
        ...(fire ? { fire } : {}),
        handled: i,
        ...(nextTime !== undefined ? { nextAtMinute: hhmmToMinutes(nextTime) } : {}),
    };
}
//# sourceMappingURL=seedling.js.map